<template>
  <div>
    <Card>
      <h2>运行日志页面 (Running Logs Page)</h2>
      <p>设备ID: {{ deviceId }}</p>
      <Row class="search-con" :gutter="16" style="margin-bottom: 16px;">
        <Col span="8">
          <DatePicker
            type="date"
            v-model="date"
            format="yyyy-MM-dd"
            placeholder="选择日期"
            style="width: 100%"
            @on-change="handleDateChange"
          />
        </Col>
        <Col span="8">
          <Input v-model="stopDesc" placeholder="输入关机描述关键词" clearable style="width: 100%" />
        </Col>
        <Col span="4">
          <Button type="primary" @click="fetchData">搜索</Button>
          <Button style="margin-left: 8px" @click="resetDate">重置</Button>
          <Button style="margin-left: 8px" type="success" @click="exportSelectedExcel">导出选中excel表格</Button>
        </Col>
      </Row>
      <Row style="margin-bottom: 16px;">
        <Col span="24">
          <Alert type="info" show-icon>
            <template #desc>
              <span>本页汇总：</span>
              <span>运行时长：{{ $options.methods.formatDuration(summary.run_time_count) }}，</span>
              <span>停机时长：{{ $options.methods.formatDuration(summary.stop_time_count) }}，</span>
              <span>无布停机时长：{{ $options.methods.formatDuration(summary.not_cloth_time_count) }}</span>
            </template>
          </Alert>
        </Col>
      </Row>
      <tables :columns="columns" v-model="tableData" :loading="loading" @on-selection-change="onSelectionChange" />
      <Page
        :current="page"
        :total="total"
        :page-size="pageSize"
        :page-size-opts="[10, 20, 50, 100, 500, 1000, 99999]"
        show-sizer
        show-elevator
        @on-change="changePage"
        @on-page-size-change="changePageSize"
        style="margin-top: 16px; text-align: right;"
      />
    </Card>
  </div>
</template>

<script>
import Tables from '_c/tables/table-only.vue'
import { getMachineRunLogList } from "@/api/data";
import { Alert, Row, Col } from 'iview'

function formatTime(ts) {
  if (!ts) return ''
  const date = new Date(ts)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  const s = String(date.getSeconds()).padStart(2, '0')
  return `${y}-${m}-${d} ${h}:${min}:${s}`
}

export default {
  name: 'running-logs',
  components: { Tables },
  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    return {
      deviceId: '',
      loading: false,
      columns: [
        { type: 'selection', width: 60, align: 'center' },
        { title: '开始运行时间', key: 'start_run_time', align: 'center', render: (h, params) => h('span', formatTime(params.row.start_run_time)) },
        { title: '停止运行时间', key: 'stop_run_time', align: 'center', render: (h, params) => h('span', formatTime(params.row.stop_run_time)) },
        { title: '运行时长', key: 'run_time_count', align: 'center', render: (h, params) => h('span', this.formatDuration(params.row.run_time_count)) },
        { title: '停机时长', key: 'stop_time_count', align: 'center', render: (h, params) => h('span', this.formatDuration(params.row.stop_time_count)) },
        { title: '无布停机时长', key: 'not_cloth_time_count', align: 'center', render: (h, params) => h('span', this.formatDuration(params.row.not_cloth_time_count)) },
        { title: '停机类型', key: 'type', align: 'center', render: (h, params) => h('span', this.stopTypeText(params.row.type)) },
        { title: '描述', key: 'stop_desc', align: 'center' }
      ],
      tableData: [],
      page: 1,
      pageSize: 20,
      total: 0,
      date: todayStr,
      stopDesc: '',
      summary: {
        run_time_count: 0,
        stop_time_count: 0,
        not_cloth_time_count: 0
      },
      selectedRows: []
    }
  },
  methods: {
    fetchData() {
      this.loading = true
      let params = {
        deviceId: this.deviceId,
        page: this.page,
        pageSize: this.pageSize
      }
      if (this.date) {
        // 兼容Date对象和字符串
        let d = this.date
        if (typeof d === 'object' && d instanceof Date) {
          const yyyy = d.getFullYear()
          const mm = String(d.getMonth() + 1).padStart(2, '0')
          const dd = String(d.getDate()).padStart(2, '0')
          d = `${yyyy}-${mm}-${dd}`
        }
        params.date = d
      }
      if (this.stopDesc) {
        params.stopDesc = this.stopDesc
      }
      
      getMachineRunLogList(params).then(res => {
        if (res.data.code === 200) {
          this.tableData = res.data.data.records
          this.total = res.data.data.total
          this.calcSummary()
        } else {
          this.tableData = []
          this.total = 0
          this.calcSummary()
        }
        this.loading = false
      }).catch(() => {
        this.tableData = []
        this.total = 0
        this.calcSummary()
        this.loading = false
      })
    },
    calcSummary() {
      let run = 0, stop = 0, notCloth = 0
      this.tableData.forEach(row => {
        const runTime = parseFloat(row.run_time_count) || 0
        const stopTime = parseFloat(row.stop_time_count) || 0
        const notClothTime = parseFloat(row.not_cloth_time_count) || 0
        
        run += runTime
        stop += stopTime
        notCloth += notClothTime
      })
      this.summary.run_time_count = run
      this.summary.stop_time_count = stop
      this.summary.not_cloth_time_count = notCloth
    },
    formatDuration(seconds) {
      if (seconds == null || isNaN(seconds)) return ''
      if (seconds < 60) return seconds + ' 秒'
      if (seconds < 3600) return (seconds / 60).toFixed(1).replace(/\.0$/, '') + ' 分钟'
      return (seconds / 3600).toFixed(2).replace(/\.00$/, '') + ' 小时'
    },
    stopTypeText(type) {
      switch(type) {
        case 0: return '普通停机';
        case 1: return '疵点停机';
        case 2: return '无布停机';
        default: return type;
      }
    },
    changePage(page) {
      this.page = page
      this.fetchData()
    },
    changePageSize(size) {
      this.pageSize = size
      this.page = 1
      this.fetchData()
    },
    handleDateChange() {
      this.page = 1
      this.fetchData()
    },
    resetDate() {
      this.date = ''
      this.stopDesc = ''
      this.page = 1
      this.fetchData()
    },
    exportExcel() {
      // 移除该方法
    },
    exportSelectedExcel() {
      if (!this.selectedRows.length) {
        this.$Message.warning('请先选择要导出的行');
        return;
      }
      const columns = this.columns.filter(col => !col.type).map(col => col.title)
      const keys = this.columns.filter(col => !col.type).map(col => col.key)
      let csv = columns.join(',') + '\n'
      this.selectedRows.forEach(row => {
        csv += keys.map(k => {
          let val = row[k]
          if (k === 'run_time_count' || k === 'stop_time_count' || k === 'not_cloth_time_count') {
            val = this.formatDuration(val)
          }
          if (k === 'type') {
            val = this.stopTypeText(row[k])
          }
          if (val === undefined || val === null) val = ''
          return '"' + String(val).replace(/"/g, '""') + '"'
        }).join(',') + '\n'
      })
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = '选中运行日志.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    onSelectionChange(selection) {
      this.selectedRows = selection
    }
  },
  mounted() {
    this.deviceId = this.$route.query.port
    this.fetchData()
  }
}
</script>

<style scoped>
h2 {
  margin-bottom: 16px;
}
</style> 